import { diContainer } from 'shared/infrastructure/di-container';
import { ProviderFactory } from 'shared/providers/provider';
import { Flowstep, Operation, ProviderType } from 'shared/types/flowstep';
import { ProviderUrlMeta } from './provider-url-meta';
import { detectFlowstep, detectRange } from './range-detection';
import { OfficeJsWorkbookApi } from './workbook-api/office-js-workbook-api';

export const openHubspot = async () => {
  const providerFactory = diContainer.resolve<ProviderFactory>('providerFactory');

  const requestData = {
    objectId: undefined as string | undefined,
    flowstep: undefined as Flowstep | undefined,
  };

  const collectRequestData = async () => {
    await Excel.run(async (context) => {
      const workbookApi = new OfficeJsWorkbookApi(context);
      const activeCell = await workbookApi.getActiveCellInfo();

      const rangeDetection = await detectRange({
        cellAddress: activeCell.address,
        worksheetId: activeCell.worksheetId,
      });
      if (!rangeDetection) {
        return;
      }

      const { targetRangeAddress, targetWorksheetId } = rangeDetection;

      const flowstep = detectFlowstep({
        targetRangeAddress,
        worksheetId: targetWorksheetId,
        operation: Operation.PULL,
      });
      if (!flowstep) {
        return;
      }
      requestData.flowstep = flowstep;
      //Create an intermediate shared provider for HubSpot and, during the merge with Salesforce, make the function provider-independent.
      const providerMeta = providerFactory.getProviderMeta(flowstep.provider);

      if (providerMeta.isIdValue(activeCell.value)) {
        requestData.objectId = activeCell.value;
        return;
      }

      if (ProviderUrlMeta.canGetIdFromFlowstep(flowstep.provider)) {
        // For Hubspot List, we can get the ID from the flowstep options. Need to refactor flowstep type shape or add extractor function.
        requestData.objectId = flowstep.providerOptions.objectType;
        return;
      }

      const range = await workbookApi.getRangeByAddress(targetRangeAddress.addressWithSheet);
      if (!range) {
        return;
      }

      const fields = flowstep.providerOptions.objectProps;
      const idColumnName = providerMeta.getIdFieldName();
      const idColumnIndex = fields.findIndex((fieldName: string) => fieldName === idColumnName);

      if (idColumnIndex === -1) {
        return;
      }

      const idCellValue = await workbookApi.getRangeCellValue(range, 1, idColumnIndex);
      if (providerMeta.isIdValue(idCellValue)) {
        requestData.objectId = idCellValue;
      }
    });
  };

  await collectRequestData();

  const provider = providerFactory.getProviderClient(requestData.flowstep?.provider || ProviderType.HS);
  const url = await provider.buildUrl(requestData);
  if (Office.context.platform !== Office.PlatformType.OfficeOnline) {
    Office.context.ui.openBrowserWindow(url);
  } else {
    window.open(url, '_blank');
  }
};
